import { createConfig } from "./config.js";
import { initTracking, processPendingEvents } from "./core.js";
import { setConsentStatus, retryFailedEvents } from "./utils.js";
import {
  initDebugMode,
  isDebugEnabled,
  log,
  createDebugPanel,
} from "./debug-utils.js";

/**
 * VerifiedTracking - Main entry point
 */
(function () {
  // Create default configuration
  const config = createConfig(window.VerifiedTrackingConfig || {});

  // Initialize debug mode
  initDebugMode(config);

  if (config.warnings) {
    config.warnings.map((warning) =>
      log("The provided configuration contains issues:", "warn", warning)
    );
  }

  // Initialize tracking controller
  let trackingController = null;

  // Public API
  window.VerifiedTracking = {
    // Store for pending events that wait for consent
    _pendingEvent: null,

    /**
     * Set GDPR consent status
     * @param {Boolean} hasConsent - Whether user has given consent
     */
    setConsent: function (hasConsent) {
      // Exit early if we've already sent this event in this session
      if (sessionStorage.getItem(config.storageKeys.eventSent) === "true") {
        log("Event already sent in this session, exiting", "info");
        return null;
      }

      setConsentStatus(hasConsent, config.storageKeys.consentGiven);

      // Process pending events if consent is given
      if (hasConsent) {
        processPendingEvents(config);
      }
    },

    /**
     * Initialize tracking manually (if not done automatically)
     * @param {Object} customConfig - Optional custom configuration
     * @returns {Object} - API interface
     */
    init: function (customConfig) {
      // Update config if custom options provided
      if (customConfig) {
        Object.assign(config, customConfig);

        // Re-initialize debug mode with updated config
        initDebugMode(config);
      }

      log("VerifiedTracking initialized", "info", config);

      // Initialize tracking
      trackingController = initTracking(config);

      // Create debug panel if debug is enabled and showPanel is true
      if (isDebugEnabled() && config.debugShowPanel) {
        createDebugPanel();
      }

      return this;
    },

    /**
     * Stop tracking
     * @returns {Object} - API interface
     */
    stop: function () {
      if (trackingController) {
        trackingController.stop();
        log("Tracking stopped manually", "info");
      }
      return this;
    },

    /**
     * Resume tracking
     * @returns {Object} - API interface
     */
    resume: function () {
      if (trackingController) {
        trackingController.start();
        log("Tracking resumed manually", "info");
      }
      return this;
    },

    /**
     * Get current tracking status and statistics
     * @returns {Object} - Tracking statistics
     */
    getStatus: function () {
      if (
        trackingController &&
        typeof trackingController.getStats === "function"
      ) {
        return trackingController.getStats();
      }
      return {
        active: !!trackingController,
        debugEnabled: isDebugEnabled(),
      };
    },

    /**
     * Toggle debug panel visibility
     * @returns {Object} - API interface
     */
    toggleDebugPanel: function () {
      const debugPanel = document.getElementById("verified-tracking-debug");

      if (debugPanel) {
        debugPanel.style.display =
          debugPanel.style.display === "none" ? "block" : "none";
        log("Debug panel visibility toggled", "info");
      } else if (isDebugEnabled()) {
        createDebugPanel();
        log("Debug panel created", "info");
      }

      return this;
    },

    /**
     * Add a custom debug message to the debug panel
     * @param {string} message - Message to display
     * @param {string} type - Message type (info, warn, error)
     * @returns {Object} - API interface
     */
    debug: function (message, type = "info") {
      if (isDebugEnabled()) {
        log(`Custom debug: ${message}`, type);
      }
      return this;
    },
  };

  // Initialize on page load
  function onLoad() {
    log("Page loaded, initializing VerifiedTracking", "info");

    // Create debug panel if debug is enabled and showPanel is true
    if (isDebugEnabled() && config.debugShowPanel) {
      createDebugPanel();
    }

    // Retry any failed events from previous sessions
    retryFailedEvents(config.storageKeys.eventSent).catch((error) => {
      log("Error retrying failed events", "error", error);
    });

    trackingController = initTracking(config);
  }

  // Initialize on page load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onLoad);
  } else {
    onLoad();
  }
})();

// Export the module for environments that support ES modules
export default window.VerifiedTracking;
